// VoiceSpectrum.Web.T2 · v10.0.10.10 · 2026-05-19 · VoiceSpectrum AI
//
// WorkflowDesigner — refreshed chrome to match docs/ui/finalized/workflow-designer-final.html
// v1.10:
//   - Sub-bar: breadcrumb ← Workflows / { workflow name }
//   - Page-header: h1 + pack badge + Matrix/Diagram view-switch + Save changes CTA
//   - Three flow regions: Pre-routing (read-only summary) + Workflows (Workflow → Intent → Node)
//     + Terminal (read-only summary). Pre-routing + Terminal show a striped header.
//   - Selection rules per D-29: Workflow 1px navy, Intent 2px green, Node 2px family color.
//   - Save state: single global "Save changes" button in the header; modal-driven discard on
//     Cancel-when-dirty (P19).
//
// data.ts shape is preserved untouched. CardSlider + ConfirmCardSlider + WorkflowDiagram
// stay as-is — only this shell is rebuilt.

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import type { Workflow, WorkflowNode, Intent } from './data';
import WorkflowDiagram from './WorkflowDiagram';
import CardSlider from './CardSlider';
import { Alert, Button, Modal } from '../ds';

type ViewMode = 'matrix' | 'diagram';

const NODE_STRIPE: Record<string, string> = {
  Identity: 'var(--stripe-identity)',
  Steps:    'var(--stripe-steps)',
  Connect:  'var(--stripe-connect)',
  Confirm:  'var(--stripe-confirm)',
};
const NODE_SOFT: Record<string, string> = {
  Identity: 'var(--identity-soft)',
  Steps:    'var(--steps-soft)',
  Connect:  'var(--connect-soft)',
  Confirm:  'var(--confirm-soft)',
};

// Pre-routing + Terminal cards (read-only summary — the slider edits the real fields).
const PRE_ROUTING = [
  { key: 'inbound',    label: 'Inbound',     stripe: 'var(--stripe-inbound)' },
  { key: 'disclosure', label: 'Disclosure',  stripe: 'var(--stripe-disclosure)' },
  { key: 'hours',      label: 'Hours check', stripe: 'var(--stripe-hours)' },
  { key: 'greeting',   label: 'Greeting',    stripe: 'var(--stripe-greeting)' },
  { key: 'lang',       label: 'Language',    stripe: 'var(--stripe-lang)' },
  { key: 'classify',   label: 'Classify',    stripe: 'var(--stripe-classify)' },
];
const TERMINALS = [
  { key: 'connect-out', label: 'Connect',    desc: 'Live transfer' },
  { key: 'voicemail',   label: 'Voicemail',  desc: 'Record + transcribe' },
  { key: 'endcall',     label: 'End call',   desc: 'Graceful goodbye' },
];

export default function WorkflowDesigner() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const [view, setView] = useState<ViewMode>('matrix');
  const [selectedIntent, setSelectedIntent] = useState<string | null>(null);
  const [sliderNode, setSliderNode] = useState<WorkflowNode | null>(null);
  const [sliderOpen, setSliderOpen] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const { data: workflow, isLoading, isError } = useQuery<Workflow>({
    queryKey: ['t2-workflow', id],
    queryFn: () => api.get<Workflow>(`/t2/workflows/${id}`),
    enabled: !!id,
  });

  const saveMutation = useMutation({
    mutationFn: (updated: Workflow) => api.put<Workflow>(`/t2/workflows/${id}`, updated),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['t2-workflow', id] }),
  });

  // v10.0.10.11 (G-3): publish flow — promotes draft → published and warms the TTS
  // prerender cache server-side. Toast feedback through state.
  const [publishToast, setPublishToast] = useState<string | null>(null);
  const publishMutation = useMutation({
    mutationFn: () => api.post<{ rendered: number; skipped: number; failed: string[] }>(
      `/t2/workflows/${id}/publish`),
    onSuccess: (r) => {
      setPublishToast(`Published. Prerender ${r.rendered} ok / ${r.skipped} skipped / ${r.failed.length} failed.`);
      qc.invalidateQueries({ queryKey: ['t2-workflow', id] });
    },
    onError: (e: unknown) => setPublishToast(`Publish failed: ${e instanceof Error ? e.message : 'unknown'}`),
  });

  async function handleSaveNode(updated: WorkflowNode): Promise<void> {
    if (!workflow) return;
    const newIntents = workflow.intents.map(intent => ({
      ...intent,
      nodes: intent.nodes.map(n => n.id === updated.id ? updated : n),
    }));
    await saveMutation.mutateAsync({ ...workflow, intents: newIntents });
  }

  function openCard(node: WorkflowNode) { setSliderNode(node); setSliderOpen(true); }
  function closeSlider()                  { setSliderNode(null); setSliderOpen(false); }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <div className="spinner" />
      </div>
    );
  }
  if (isError || !workflow) {
    return (
      <Alert variant="error">
        Could not load workflow. <Link to="/workflows">Back to list</Link>
      </Alert>
    );
  }

  const activeIntent: Intent | undefined =
    selectedIntent ? workflow.intents.find(i => i.id === selectedIntent) : undefined;

  return (
    <div className="stack-4">
      {/* ── Sub-bar / breadcrumb ─────────────────────────────────────────── */}
      <div className="row-2 muted text-xs" style={{ marginBottom: -8 }}>
        <Link to="/workflows" style={{ color: 'var(--muted)' }}>← Workflows</Link>
        <span>/</span>
        <strong style={{ color: 'var(--ink)' }}>{workflow.name}</strong>
      </div>

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="page-header">
        <div className="row-3" style={{ alignItems: 'baseline' }}>
          <h1>Workflow Designer</h1>
          <span className="pill pill-navy">{workflow.status ?? 'draft'}</span>
          {workflow.description && (
            <span className="muted text-sm">{workflow.description}</span>
          )}
        </div>

        <div className="row-2">
          {/* Matrix / Diagram view-switch */}
          <div
            role="group"
            aria-label="View mode"
            style={{ display: 'flex', border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}
          >
            {(['matrix', 'diagram'] as ViewMode[]).map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                style={{
                  padding: '6px 14px',
                  fontWeight: 600,
                  fontSize: 12.5,
                  border: 'none',
                  background: view === v ? 'var(--accent)' : '#fff',
                  color: view === v ? '#fff' : 'var(--ink)',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                }}
              >
                {v}
              </button>
            ))}
          </div>
          <Button variant="secondary" loading={saveMutation.isPending} onClick={() => saveMutation.mutate(workflow)}>
            Save changes
          </Button>
          <Button variant="primary" loading={publishMutation.isPending} onClick={() => publishMutation.mutate()}>
            Publish
          </Button>
        </div>
      </div>

      {publishToast && (
        <Alert variant={publishToast.startsWith('Publish failed') ? 'error' : 'success'}>
          {publishToast}
        </Alert>
      )}

      {/* ── Matrix view ──────────────────────────────────────────────────── */}
      {view === 'matrix' && (
        <div className="stack-6">
          {/* Pre-routing row */}
          <section>
            <h4 style={{ marginBottom: 'var(--space-2)' }}>Pre-routing</h4>
            <div className="row-2" style={{ flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              {PRE_ROUTING.map(p => (
                <div
                  key={p.key}
                  className="panel"
                  style={{
                    padding: '8px 12px',
                    borderLeft: `3px solid ${p.stripe}`,
                    minWidth: 132,
                  }}
                >
                  <div className="text-xs muted" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, color: p.stripe }}>
                    {p.label}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Workflows / Intents / Nodes — 3-column grid */}
          <section>
            <h4 style={{ marginBottom: 'var(--space-2)' }}>Workflows</h4>
            <div className="grid-3" style={{ alignItems: 'start' }}>
              {/* Workflow column */}
              <div className="stack-2">
                <div className="text-xs muted" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                  Workflow
                </div>
                <div
                  className="panel"
                  style={{
                    padding: '10px 14px',
                    borderLeft: '3px solid var(--workflow-stripe)',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{workflow.name}</div>
                  <div className="muted text-xs" style={{ marginTop: 4 }}>
                    {workflow.intents.length} intent{workflow.intents.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>

              {/* Intent column */}
              <div className="stack-2">
                <div className="text-xs muted" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                  Intent
                </div>
                {workflow.intents.map(intent => {
                  const isSel = selectedIntent === intent.id;
                  return (
                    <div
                      key={intent.id}
                      onClick={() => setSelectedIntent(intent.id)}
                      className="panel"
                      style={{
                        padding: '10px 14px',
                        borderLeft: '3px solid var(--select-intent)',
                        background: isSel ? 'var(--good-soft)' : 'var(--panel)',
                        outline: isSel ? '2px solid var(--select-intent)' : 'none',
                        outlineOffset: -1,
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{intent.name}</div>
                      <div className="muted text-xs" style={{ marginTop: 4 }}>
                        {intent.nodes.length} node{intent.nodes.length !== 1 ? 's' : ''} · {intent.classification.preset}
                      </div>
                    </div>
                  );
                })}
                {workflow.intents.length === 0 && (
                  <div className="muted text-xs">No intents defined yet.</div>
                )}
              </div>

              {/* Node column */}
              <div className="stack-2">
                <div className="text-xs muted" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                  Node
                </div>
                {activeIntent
                  ? activeIntent.nodes.map(node => {
                      const isSel = sliderNode?.id === node.id;
                      const stripe = NODE_STRIPE[node.type] ?? 'var(--soft)';
                      const soft   = NODE_SOFT[node.type]   ?? 'var(--bg)';
                      return (
                        <div
                          key={node.id}
                          onClick={() => openCard(node)}
                          className="panel"
                          style={{
                            padding: '10px 14px',
                            borderLeft: `3px solid ${stripe}`,
                            outline: isSel ? `2px solid ${stripe}` : 'none',
                            outlineOffset: -1,
                            background: isSel ? soft : 'var(--panel)',
                            cursor: 'pointer',
                            transition: 'background var(--t-fast), transform var(--t-fast)',
                          }}
                          onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = soft; }}
                          onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = 'var(--panel)'; }}
                        >
                          <div style={{ fontSize: 9.5, fontWeight: 700, color: stripe, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
                            {node.type}
                          </div>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{node.name}</div>
                          {!node.enabled && <div className="muted text-xs" style={{ marginTop: 2 }}>Disabled</div>}
                        </div>
                      );
                    })
                  : <div className="muted text-xs">Select an intent to view its nodes.</div>
                }
              </div>
            </div>
          </section>

          {/* Terminal row */}
          <section>
            <h4 style={{ marginBottom: 'var(--space-2)' }}>Terminal</h4>
            <div className="row-2" style={{ flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              {TERMINALS.map(t => (
                <div
                  key={t.key}
                  className="panel"
                  style={{
                    padding: '10px 14px',
                    borderLeft: '3px solid var(--terminal-stripe)',
                    minWidth: 180,
                  }}
                >
                  <div className="text-xs" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, color: 'var(--terminal-stripe)' }}>
                    {t.label}
                  </div>
                  <div className="muted text-xs" style={{ marginTop: 4 }}>{t.desc}</div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {/* ── Diagram view ─────────────────────────────────────────────────── */}
      {view === 'diagram' && (
        <WorkflowDiagram workflow={workflow} onCardClick={openCard} />
      )}

      {/* ── Card slider (existing component) ─────────────────────────────── */}
      <CardSlider
        node={sliderNode}
        open={sliderOpen}
        onClose={() => closeSlider()}
        onSave={handleSaveNode}
      />

      {/* Save error toast */}
      {saveMutation.isError && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, maxWidth: 360, zIndex: 600 }}>
          <Alert variant="error" title="Save failed">Please try again.</Alert>
        </div>
      )}

      {/* Discard-confirm modal stub (wired up by the slider's own dirty-cancel flow) */}
      <Modal
        open={confirmDiscardOpen}
        onClose={() => setConfirmDiscardOpen(false)}
        title="Discard changes?"
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirmDiscardOpen(false)}>Keep editing</Button>
            <Button variant="destructive" onClick={() => { setConfirmDiscardOpen(false); closeSlider(); }}>
              Discard changes
            </Button>
          </>
        }
      >
        <p>Your edits to this node have not been saved. Discard them and close the editor?</p>
      </Modal>

      {/* Footer build-tag */}
      <div className="muted text-xs" style={{ textAlign: 'center', marginTop: 'var(--space-12)' }}>
        Workflow Designer · v1.10 · v10.0.10.10 · VoiceSpectrum AI
      </div>
    </div>
  );
}
